import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'

/**
 * Service attendance — the Sunday count.
 *
 * Recording is a plain mutation rather than a queued one: a service count is
 * entered once, by someone who can see whether it saved, and re-recording the
 * same service corrects it server-side. The group roster is the write that
 * needed a queue; this one does not pretend to.
 */

export interface SessionFilters {
  from?: string
  to?: string
  campusId?: string
}

export const serviceAttendanceKeys = {
  all: () => ['attendance'] as const,
  sessions: (filters: SessionFilters = {}) =>
    [...serviceAttendanceKeys.all(), 'sessions', filters] as const,
  session: (id: string) => [...serviceAttendanceKeys.all(), 'session', id] as const,
  trend: (query: Record<string, unknown> = {}) =>
    [...serviceAttendanceKeys.all(), 'trend', query] as const,
  groupReport: (query: Record<string, unknown> = {}) =>
    [...serviceAttendanceKeys.all(), 'group-report', query] as const,
}

export function useServiceSessions(filters: SessionFilters = {}) {
  const api = useApi()
  const query = useInfiniteQuery({
    queryKey: serviceAttendanceKeys.sessions(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.attendance.sessions.get({
        query: { ...filters, ...(pageParam ? { cursor: pageParam } : {}) },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })

  return {
    sessions: query.data?.pages.flatMap((page) => page.items) ?? [],
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isPending,
    error: query.error,
  }
}

export type ServiceSession = ReturnType<typeof useServiceSessions>['sessions'][number]

export function useAttendanceTrend(
  query: { from?: string; to?: string; campusId?: string; window?: number } = {},
) {
  const api = useApi()
  return useQuery({
    queryKey: serviceAttendanceKeys.trend(query),
    queryFn: async () => {
      const { data, error } = await api.api.v1.attendance.trend.get({ query })
      if (error) throw error
      return data
    },
  })
}

export type AttendanceTrend = NonNullable<ReturnType<typeof useAttendanceTrend>['data']>

/** Every group's attendance side by side — worst first, from the server. */
export function useGroupAttendanceReport(query: { from?: string; to?: string } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: serviceAttendanceKeys.groupReport(query),
    queryFn: async () => {
      const { data, error } = await api.api.v1.groups.attendance.report.get({ query })
      if (error) throw error
      return data
    },
  })
}

export interface RecordSessionInput {
  name: string
  sessionDate: string
  campusId?: string | null
  startsAt?: string | null
  note?: string | null
  /** The check-in service it counts, so its check-ins show beside it. */
  checkinTimeId?: string | null
  counts?: Record<string, number>
}

function useSessionInvalidation() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: serviceAttendanceKeys.all() })
}

/** Re-recording the same name, campus and date corrects that service. */
export function useRecordService() {
  const api = useApi()
  const invalidate = useSessionInvalidation()
  return useMutation({
    mutationFn: async (body: RecordSessionInput) => {
      const { data, error } = await api.api.v1.attendance.sessions.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

export function useUpdateService() {
  const api = useApi()
  const invalidate = useSessionInvalidation()
  return useMutation({
    mutationFn: async ({ id, ...body }: Partial<RecordSessionInput> & { id: string }) => {
      const { data, error } = await api.api.v1.attendance.sessions({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

export function useDeleteService() {
  const api = useApi()
  const invalidate = useSessionInvalidation()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.attendance.sessions({ id }).delete()
      if (error) throw error
    },
    onSuccess: () => void invalidate(),
  })
}
